<template>
  <div class="min-h-[100dvh] bg-[#f6f7f9] py-12">
    <div class="max-w-[640px] mx-auto px-6">

      <!-- Logo -->
      <div class="flex justify-center mb-8">
        <router-link to="/" aria-label="Plug2Charge.nl - home" class="no-underline">
          <Logo :height="40" />
        </router-link>
      </div>

      <!-- Flow progress - stap 3 -->
      <div class="flex items-center justify-center gap-2 mb-8">
        <div class="flex items-center gap-2">
          <div class="w-2.5 h-2.5 rounded-full bg-green-400"></div>
          <span class="text-sm font-medium text-[#aaa]">Winkelwagen</span>
        </div>
        <div class="w-8 h-px bg-[#ddd]"></div>
        <div class="flex items-center gap-2">
          <div class="w-2.5 h-2.5 rounded-full bg-green-400"></div>
          <span class="text-sm font-medium text-[#aaa]">Bestellen</span>
        </div>
        <div class="w-8 h-px bg-[#ddd]"></div>
        <div class="flex items-center gap-2">
          <div class="w-2.5 h-2.5 rounded-full bg-[#FFD500] shadow-[0_0_0_3px_rgba(255,213,0,0.25)]"></div>
          <span class="text-sm font-bold text-[#111]">Bevestiging</span>
        </div>
      </div>

      <!-- Success card -->
      <div class="bg-white rounded-2xl border border-[#e9eaec] overflow-hidden shadow-sm mb-5">

        <!-- Green top bar -->
        <div class="h-1.5 bg-gradient-to-r from-green-400 to-green-500"></div>

        <div class="px-8 pt-10 pb-8 text-center">

          <!-- Animated checkmark -->
          <div class="w-20 h-20 rounded-full bg-green-50 border-4 border-green-400 flex items-center justify-center mx-auto mb-6 check-animate">
            <i class="bi bi-check-circle text-[2.5rem] text-green-500"></i>
          </div>

          <h1 class="text-[1.75rem] font-extrabold text-[#111] mb-2">Bedankt, {{ order.firstName || 'beste klant' }}!</h1>
          <p class="text-[#666] text-[0.95rem] mb-6">Je bestelling is succesvol geplaatst en wordt zo snel mogelijk verwerkt.</p>

          <!-- Order number badge -->
          <div class="inline-flex items-center gap-2 bg-[#fffbeb] border border-[#FFD500]/40 text-[#111] font-bold px-5 py-2.5 rounded-full mb-8">
            <i class="bi bi-box-seam text-[#FFD500]"></i>
            Bestelnummer #{{ order.number || '...' }}
          </div>

          <!-- Info row -->
          <div class="grid grid-cols-2 gap-4 mb-8">
            <div class="bg-[#f8f9fa] rounded-xl p-4 text-center">
              <i class="bi bi-envelope text-[1.4rem] text-[#FFD500] mb-2 block"></i>
              <p class="text-[0.8rem] font-semibold text-[#333] mb-0.5">Bevestiging onderweg</p>
              <p class="text-[0.73rem] text-[#888]">Check je inbox voor alle details</p>
            </div>
            <div class="bg-[#f8f9fa] rounded-xl p-4 text-center">
              <i class="bi bi-truck text-[1.4rem] text-[#FFD500] mb-2 block"></i>
              <p class="text-[0.8rem] font-semibold text-[#333] mb-0.5">Levering</p>
              <p class="text-[0.73rem] text-[#888]">Verwacht binnen 1-3 werkdagen</p>
            </div>
          </div>

          <!-- CTAs -->
          <div class="flex gap-3 justify-center flex-wrap">
            <router-link
              to="/"
              class="inline-flex items-center gap-2 bg-[#FFD500] hover:bg-[#ffb800] text-[#111] font-bold px-6 py-3 rounded-xl no-underline thankyou-btn-primary"
            >
              <i class="bi bi-house"></i>
              Naar de homepage
            </router-link>
            <router-link
              to="/producten"
              class="inline-flex items-center gap-2 bg-white border border-[#e5e7eb] hover:border-[#FFD500] text-[#333] font-semibold px-6 py-3 rounded-xl no-underline transition-colors"
            >
              <i class="bi bi-grid"></i>
              Verder winkelen
            </router-link>
          </div>
        </div>
      </div>

      <!-- Wat nu? -->
      <div class="bg-white rounded-2xl border border-[#e9eaec] px-6 py-5">
        <h2 class="text-[1rem] font-bold text-[#111] mb-4 flex items-center gap-2">
          <i class="bi bi-calendar text-[#FFD500]"></i>
          Wat gebeurt er nu?
        </h2>
        <ol class="flex flex-col gap-4">
          <li v-for="(step, i) in steps" :key="i" class="flex items-start gap-3">
            <div class="w-6 h-6 rounded-full bg-[#FFD500] text-[#111] text-[0.7rem] font-bold flex items-center justify-center shrink-0 mt-0.5">
              {{ i + 1 }}
            </div>
            <div>
              <p class="text-[0.875rem] font-semibold text-[#111]">{{ step.title }}</p>
              <p class="text-[0.78rem] text-[#888]">{{ step.desc }}</p>
            </div>
          </li>
        </ol>
      </div>

      <!-- Review uitnodiging -->
      <div class="bg-white rounded-2xl border border-[#e9eaec] px-6 py-5 mt-4">
        <h2 class="text-[1rem] font-bold text-[#111] mb-1 flex items-center gap-2">
          <i class="bi bi-star-fill text-[#FFD500]"></i>
          Deel jouw ervaring
        </h2>
        <p class="text-[0.82rem] text-[#888] mb-4">Je helpt andere klanten enorm met een korte review. Het duurt minder dan 1 minuut.</p>
        <div class="flex gap-3 flex-wrap">
          <a
            href="https://g.page/r/plug2charge/review"
            target="_blank"
            rel="noopener noreferrer"
            class="flex-1 min-w-[120px] flex items-center justify-center gap-2 bg-[#4285F4] hover:bg-[#3367d6] text-white font-semibold text-[0.82rem] py-2.5 px-4 rounded-xl no-underline transition-colors"
          >
            <i class="bi bi-google"></i>
            Google review
          </a>
          <a
            href="https://www.trustpilot.com/review/plug2charge.nl"
            target="_blank"
            rel="noopener noreferrer"
            class="flex-1 min-w-[120px] flex items-center justify-center gap-2 bg-[#00b67a] hover:bg-[#009e6a] text-white font-semibold text-[0.82rem] py-2.5 px-4 rounded-xl no-underline transition-colors"
          >
            <i class="bi bi-star-fill"></i>
            Trustpilot review
          </a>
        </div>
      </div>

      <!-- Contact -->
      <div class="mt-5 flex items-center justify-center gap-2 text-[0.82rem] text-[#888]">
        <i class="bi bi-headset text-[#FFD500]"></i>
        Vragen? Mail <a href="mailto:info@plug2charge.nl" class="text-[#111] font-semibold no-underline hover:text-[#FFD500] transition-colors">info@plug2charge.nl</a>
        of start een <a href="/klantenservice#livechat" class="text-[#111] font-semibold no-underline hover:text-[#FFD500] transition-colors">live chat</a>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAnalytics } from '@composables/useAnalytics'
import { useCartStore } from '@stores/cart'
import Logo from '@components/Logo.vue'

const route = useRoute()
const router = useRouter()
const { trackPurchase } = useAnalytics()
const cartStore = useCartStore()

// Lees order info uit sessionStorage (gezet door Checkout.vue)
// Gebruik NOOIT het ordernummer in de URL - gebruik de order_key (random hash) als fallback
const order = ref({ id: null, number: null, total: null, email: null, firstName: null })
const loading = ref(false)

const PAID = ['processing', 'completed', 'on-hold']

onMounted(async () => {
  const key = route.query.key
  let confirmed = false

  // Verifieer de échte betaalstatus. Mollie stuurt na ANNULEREN óók naar deze
  // pagina, dus we tonen pas 'Bedankt' als de order daadwerkelijk betaald is.
  if (key) {
    loading.value = true
    try {
      const res = await fetch(`/api/order/by-key?key=${encodeURIComponent(key)}`)
      if (res.ok) {
        const data = await res.json()
        if (!PAID.includes(data.status)) {
          // Niet betaald (geannuleerd / nog in behandeling) → naar de statuspagina
          return router.replace(`/betaling-status?key=${encodeURIComponent(key)}`)
        }
        confirmed = true
        order.value = {
          id:        data.id,
          number:    data.number,
          total:     data.total,
          email:     data.billing?.email,
          firstName: data.billing?.first_name,
        }
      }
    } catch (e) {
      console.warn('Could not fetch order by key:', e)
    } finally {
      loading.value = false
    }
  }

  // Naam + analytics aanvullen vanuit sessionStorage (gezet door Checkout).
  try {
    const stored = sessionStorage.getItem('lastOrder')
    if (stored) {
      const parsed = JSON.parse(stored)
      order.value = { ...parsed, ...order.value }
      sessionStorage.removeItem('lastOrder')
      if (!key) confirmed = true   // legacy succes-signaal zonder key
      trackPurchase(parsed, parsed.items || [], parsed.shipping || 0)
    }
  } catch (e) {
    console.warn('Could not read order from session:', e)
  }

  // Winkelwagen pas legen bij een BEVESTIGDE betaling.
  if (confirmed) { try { cartStore.clearCart() } catch {} }
})

const steps = [
  { title: 'Orderbevestiging',      desc: 'Je ontvangt direct een bevestigingsmail met alle orderdetails.' },
  { title: 'Betaling verwerkt',     desc: 'Zodra je betaling bevestigd is, pakken we je order in.' },
  { title: 'Verzendbevestiging',    desc: 'Je krijgt een e-mail met track & trace zodra het pakket onderweg is.' },
  { title: 'Levering aan de deur',  desc: 'Verwacht je pakket binnen 1-3 werkdagen na betaling.' },
]
</script>

<style scoped>
.check-animate {
  animation: pop 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) both;
}
@keyframes pop {
  from { transform: scale(0); opacity: 0; }
  to   { transform: scale(1); opacity: 1; }
}
.thankyou-btn-primary {
  transition: background 150ms cubic-bezier(0.23, 1, 0.32, 1),
              transform 150ms cubic-bezier(0.23, 1, 0.32, 1),
              box-shadow 150ms cubic-bezier(0.23, 1, 0.32, 1);
}
.thankyou-btn-primary:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(0,0,0,0.12); }
.thankyou-btn-primary:active { transform: scale(0.98); }
@media (prefers-reduced-motion: reduce) { .thankyou-btn-primary { transition: none; } }
</style>
